"use client";

import { Idea, IdeaService } from '@/services/IdeaService';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface ValidatorContextType {
    ideaInput: string;
    setIdeaInput: (val: string) => void;
    currentIdea: Idea | null;
    setCurrentIdea: (idea: Idea | null) => void;
    ideas: Idea[];
    loadingIdeas: boolean;
    refreshIdeas: () => Promise<void>;
    loadIdea: (id: string) => Promise<void>;
    createNewIdea: () => void;
    deleteIdea: (id: string) => Promise<void>;
    isCookieBannerVisible: boolean;
    setIsCookieBannerVisible: (visible: boolean) => void;
}

const ValidatorContext = createContext<ValidatorContextType | undefined>(undefined);

export function ValidatorProvider({ children }: { children: ReactNode }) {
    const [ideaInput, setIdeaInput] = useState("");
    const [currentIdea, setCurrentIdea] = useState<Idea | null>(null);
    const [ideas, setIdeas] = useState<Idea[]>([]);
    const [loadingIdeas, setLoadingIdeas] = useState(false);
    const [isCookieBannerVisible, setIsCookieBannerVisible] = useState(false);
    const { user, loading } = useAuth();

    // Load ideas when auth state changes
    // Load ideas when auth state changes
    useEffect(() => {
        async function init() {
            if (!loading) {
                // If user is logged in, try to migrate guest ideas first
                if (user) {
                    await IdeaService.migrateGuestIdeas(user.uid);
                }
                await refreshIdeas();
            }
        }
        init();
    }, [user, loading]);

    const refreshIdeas = async () => {
        setLoadingIdeas(true);
        try {
            const fetched = await IdeaService.getIdeas(user ? user.uid : null);
            setIdeas(fetched);
        } catch (err) {
            console.error("Error fetching ideas", err);
        } finally {
            setLoadingIdeas(false);
        }
    };

    const loadIdea = async (id: string) => {
        const found = await IdeaService.getIdea(user ? user.uid : null, id);
        setCurrentIdea(found);
    };

    const createNewIdea = () => {
        setCurrentIdea(null);
        setIdeaInput("");
    };

    const deleteIdea = async (id: string) => {
        await IdeaService.deleteIdea(user ? user.uid : null, id);
        await refreshIdeas();
        if (currentIdea?.id === id) {
            createNewIdea();
        }
    };

    return (
        <ValidatorContext.Provider value={{
            ideaInput, setIdeaInput,
            currentIdea, setCurrentIdea,
            ideas, loadingIdeas,
            refreshIdeas,
            loadIdea,
            createNewIdea,
            deleteIdea,
            isCookieBannerVisible,
            setIsCookieBannerVisible
        }}>
            {children}
        </ValidatorContext.Provider>
    );
}

export function useValidator() {
    const context = useContext(ValidatorContext);
    if (context === undefined) {
        return {
            ideaInput: "",
            setIdeaInput: () => {},
            currentIdea: null,
            setCurrentIdea: () => {},
            ideas: [],
            loadingIdeas: false,
            refreshIdeas: async () => {},
            loadIdea: async () => {},
            createNewIdea: () => {},
            deleteIdea: async () => {},
            isCookieBannerVisible: false,
            setIsCookieBannerVisible: () => {}
        };
    }
    return context;
}
